import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {Aangifte, BokkzIbDataService, IncomeTaxEntity} from "@snelstart-ventures/fiscaal/domain-logic";
import {filter, Observable, shareReplay, take} from "rxjs";
import {FormControl, FormGroup} from "@angular/forms";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AsyncPipe} from "@angular/common";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-voorlopige-aanslagen',
  templateUrl: './voorlopige-aanslagen.component.html',
  styleUrls: ['./voorlopige-aanslagen.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    AsyncPipe
  ]
})
export class VoorlopigeAanslagenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  public isPartner = false;
  public isDefAangifte = false;

  public formGroup = new FormGroup({
    voorlopigeAanslag: new FormControl<number | null>(null),
    voorlopigeTeruggave: new FormControl<number | null>(null),
    voorlopigeAanslagZvw: new FormControl<number | null>(null),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
  }

  ngOnDestroy(): void {
    // rely on saveFrom in component
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: Aangifte): void {
    this.formGroup.reset({}, { emitEvent: false });
    const voorlopigeAanslag = details.voorheffingen?.voorlopigeAanslag ?? 0;

    if (voorlopigeAanslag >= 0) {
      this.formGroup.patchValue({ voorlopigeAanslag }, { emitEvent: false });
    } else {
      this.formGroup.patchValue({ voorlopigeTeruggave: voorlopigeAanslag }, { emitEvent: false });
    }
    const voorlopigeAanslagZvw = details.voorheffingen?.voorlopigeAanslagZvw ?? 0;
    if (voorlopigeAanslagZvw > 0) {
      this.formGroup.patchValue({ voorlopigeAanslagZvw: voorlopigeAanslagZvw }, { emitEvent: false });
    }
  }

  saveForm(): void {
    const values = this.formGroup.getRawValue();

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const update = (aangifte: Aangifte) => ({
        ...aangifte,
        voorheffingen: {
          ...aangifte.voorheffingen,
          voorlopigeAanslag: values.voorlopigeAanslag ? values.voorlopigeAanslag : values.voorlopigeTeruggave ? values.voorlopigeTeruggave : undefined,
          voorlopigeAanslagZvw: values.voorlopigeAanslagZvw ?? undefined,
        },
      });

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
      } : update(currentEntity.aangifte);

      return {
        ...currentEntity,
        aangifte: updatedAangifte,
      } as IncomeTaxEntity;
    });
  }
}
